import React, { useState } from "react";
import { C } from "../constants";
import {
  DAY_ACTIVITY_SLOTS,
  HOMEWORK_ACTIVITY_SLOTS,
  emptySlotOverride,
  normalizeSlotOverrides,
  splitStoredOverrides,
  catalogWithCustom,
} from "../constants/dayActivities";
import { formatTime12 } from "./FormControls";

const timeInp = {
  width: 108,
  padding: "6px 8px",
  fontSize: 12,
  border: `1px solid ${C.border}`,
  borderRadius: 7,
  color: C.text,
  background: "#fff",
};

function minutes(raw) {
  const m = String(raw || "").match(/^(\d{2}):(\d{2})$/);
  if (!m) return 9999;
  return Number(m[1]) * 60 + Number(m[2]);
}

function iconBtn(title, onClick, icon, danger = false) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        border: `1px solid ${danger ? "#FECACA" : C.border}`,
        background: danger ? "#FEF2F2" : "#fff",
        color: danger ? C.error : C.muted,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <i className={`ti ${icon}`} style={{ fontSize: 15 }} />
    </button>
  );
}

function SlotTable({ catalog, value, onChange, hint, showTimes = true, dayWindow = null, onResync = null }) {
  // keepRaw: the editor must see exactly what the user typed (no trim / no fallback)
  const overrides = normalizeSlotOverrides(value, catalog, showTimes, { keepRaw: true });
  const slots = catalogWithCustom(value, catalog);
  const [dragId, setDragId] = useState("");
  const [overId, setOverId] = useState("");

  const patch = (id, next) => {
    onChange?.({
      ...overrides,
      [id]: { ...emptySlotOverride(), ...overrides[id], ...next },
    });
  };

  const commit = (next) => onChange?.(next);

  const addActivity = () => {
    const id = `custom_${Date.now()}`;
    const lastOn = slots
      .filter((s) => (overrides[s.id] || {}).mode !== "hide")
      .map((s) => overrides[s.id] || {})
      .sort((a, b) => minutes(a.end) - minutes(b.end))
      .pop();
    const start = showTimes && lastOn?.end ? lastOn.end : "";
    commit({
      ...overrides,
      [id]: { ...emptySlotOverride(), mode: "add", title: "", notes: "", start, end: "", custom: true },
    });
  };

  const removeActivity = (id) => {
    const next = { ...overrides };
    delete next[id];
    commit(next);
  };

  const deleteActivity = (id) => {
    const row = overrides[id] || emptySlotOverride();
    if (row.custom || row.mode === "add") {
      removeActivity(id);
      return;
    }
    patch(id, { mode: "hide" });
  };

  const turnOff = (id) => deleteActivity(id);

  const updateFields = (slot, next) => {
    const current = { ...emptySlotOverride(), ...overrides[slot.id], ...next };
    const custom = !!slot.custom || current.mode === "add";
    const label = slot.label || "";
    const title = String(current.title ?? "");
    const notes = String(current.notes ?? "");
    let mode = current.mode;
    if (custom) mode = "add";
    else if (current.mode === "hide") mode = "hide";
    else if ((title.trim() && title.trim() !== label) || notes.trim()) mode = "replace";
    else mode = "keep";
    const titleSet = "title" in next ? true : Boolean(current.titleSet);
    patch(slot.id, { ...current, mode, title, notes, titleSet });
  };

  // Leaving a preset name empty restores its default; custom rows keep "" (shown as New activity)
  const restoreEmptyTitle = (slot) => {
    const row = overrides[slot.id] || emptySlotOverride();
    if (slot.custom || row.mode === "add") return;
    if (String(row.title || "").trim() === "") {
      patch(slot.id, { title: "", titleSet: false });
    }
  };

  const titleValue = (slot, row) => {
    if (slot.custom || row.mode === "add") return row.title ?? "";
    return row.titleSet ? row.title ?? "" : row.title || slot.label || "";
  };

  // Row problems: end before start, outside the Basics day, overlapping another row
  const rowIssues = (slot) => {
    if (!showTimes) return [];
    const row = overrides[slot.id] || {};
    if (!row.start && !row.end) return [];
    const a = minutes(row.start);
    const b = minutes(row.end);
    const out = [];
    if (row.start && row.end && b <= a) out.push("End must be after start");
    if (dayWindow?.start && dayWindow?.end && row.start && row.end) {
      if (a < minutes(dayWindow.start) || b > minutes(dayWindow.end)) out.push("Outside the day set in Basics");
    }
    if (row.start && row.end && b > a) {
      const clash = slots.find((o) => {
        if (o.id === slot.id) return false;
        const r = overrides[o.id] || {};
        if (r.mode === "hide" || !r.start || !r.end) return false;
        return minutes(r.start) < b && minutes(r.end) > a;
      });
      if (clash) {
        const r = overrides[clash.id] || {};
        out.push(`Overlaps ${String(r.title || clash.label || "another activity")}`);
      }
    }
    return out;
  };

  const turnOn = (id) => patch(id, { mode: "keep" });

  const swapTimes = (aId, bId) => {
    if (!aId || !bId || aId === bId) return;
    const a = { ...emptySlotOverride(), ...overrides[aId] };
    const b = { ...emptySlotOverride(), ...overrides[bId] };
    commit({
      ...overrides,
      [aId]: {
        ...a,
        start: b.start,
        end: b.end,
        mode: a.mode === "hide" ? (a.custom ? "add" : "keep") : a.mode,
      },
      [bId]: {
        ...b,
        start: a.start,
        end: a.end,
        mode: b.mode === "hide" ? (b.custom ? "add" : "keep") : b.mode,
      },
    });
  };

  const swapOrder = (aId, bId) => {
    if (!aId || !bId || aId === bId) return;
    const ids = slots.map((s) => s.id);
    const next = { ...overrides };
    const ia = ids.indexOf(aId);
    const ib = ids.indexOf(bId);
    if (ia < 0 || ib < 0) return;
    const ordered = [...ids];
    ordered[ia] = bId;
    ordered[ib] = aId;
    ordered.forEach((id, i) => {
      next[id] = { ...emptySlotOverride(), ...overrides[id], sort: i };
    });
    commit(next);
  };

  const onDropRow = (targetId) => {
    if (!dragId || dragId === targetId) return;
    if (showTimes) swapTimes(dragId, targetId);
    else swapOrder(dragId, targetId);
    setDragId("");
    setOverId("");
  };

  const onDropOff = () => {
    if (dragId) turnOff(dragId);
    setDragId("");
    setOverId("");
  };

  const onDropOn = () => {
    if (dragId) turnOn(dragId);
    setDragId("");
    setOverId("");
  };

  const onList = slots
    .filter((s) => (overrides[s.id] || {}).mode !== "hide")
    .sort((a, b) => {
      if (showTimes) {
        const da = minutes((overrides[a.id] || {}).start);
        const db = minutes((overrides[b.id] || {}).start);
        return da - db;
      }
      return ((overrides[a.id] || {}).sort ?? 0) - ((overrides[b.id] || {}).sort ?? 0);
    });
  const offList = slots.filter((s) => (overrides[s.id] || {}).mode === "hide");

  const changed = slots.some((s) => {
    const row = overrides[s.id] || {};
    const def = catalog.find((c) => c.id === s.id);
    if (!def) return true;
    if (row.mode !== "keep") return true;
    if (showTimes && (row.start !== def.start || row.end !== def.end)) return true;
    return Boolean(row.title || row.notes);
  });

  const rowWrap = (slot, { off } = {}) => {
    const row = overrides[slot.id] || emptySlotOverride();
    const defaultLabel = (catalog || []).find((item) => item.id === slot.id)?.label || "";
    const title = String(row.title || "").trim();
    const changedName = Boolean(slot.custom || row.mode === "add" || (title && title !== defaultLabel));
    const restOnly = /^(break|lunch|rest|short break)$/i.test(title || defaultLabel);
    const needsTaskDetails = changedName && !restOnly;
    const missingTaskDetails = needsTaskDetails && !String(row.notes || "").trim();
    const isOver = overId === slot.id;
    const isDrag = dragId === slot.id;
    return (
      <tr
        key={slot.id}
        onDragOver={(e) => {
          e.preventDefault();
          setOverId(slot.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDropRow(slot.id);
        }}
        style={{
          borderBottom: `1px solid ${C.border}`,
          background: isOver && !isDrag ? C.primarySoft : off ? "#F8FAFC" : "#fff",
          opacity: isDrag ? 0.45 : off ? 0.72 : 1,
          outline: isOver && !isDrag ? `2px solid ${C.primary}` : "none",
        }}
      >
        <td
          draggable
          onDragStart={(e) => {
            setDragId(slot.id);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", slot.id);
          }}
          onDragEnd={() => {
            setDragId("");
            setOverId("");
          }}
          style={{ padding: "8px 8px", width: 28, color: C.faint, cursor: "grab" }}
          title="Drag to swap or delete"
        >
          <i className="ti ti-grip-vertical" style={{ fontSize: 16 }} />
        </td>
        <td style={{ padding: "8px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className={`ti ${slot.icon || "ti-plus"}`} style={{ fontSize: 15, color: C.primary, flexShrink: 0 }} />
            <input
              type="text"
              value={titleValue(slot, row)}
              placeholder="Activity name"
              disabled={off}
              onChange={(e) => updateFields(slot, { title: e.target.value })}
              onBlur={() => restoreEmptyTitle(slot)}
              style={{ ...timeInp, width: "100%", minWidth: 140, fontWeight: 700 }}
            />
          </div>
          <textarea
            value={row.notes ?? ""}
            placeholder={needsTaskDetails
              ? "Required for a renamed activity: tell AI what task to create and what the learner should produce"
              : "Tell AI what this activity should cover (optional)"}
            disabled={off}
            rows={2}
            onChange={(e) => updateFields(slot, { notes: e.target.value })}
            aria-label={`${title || defaultLabel} task or content instructions${needsTaskDetails ? " (required)" : ""}`}
            style={{ ...timeInp, width: "100%", minWidth: 140, marginTop: 6, resize: "vertical", fontFamily: "inherit", minHeight: 58, borderColor: missingTaskDetails ? C.error : C.border }}
          />
          {needsTaskDetails && (
            <div style={{ marginTop: 4, fontSize: 11.5, lineHeight: 1.4, color: missingTaskDetails ? C.error : C.muted }}>
              {missingTaskDetails
                ? "Add the task or content so AI knows what to generate. Example: ‘Solve one beginner array problem and apply it to the search feature.’"
                : "AI will use this as the instruction for the activity. Include the task, level, and expected output."}
            </div>
          )}
          {!off && rowIssues(slot).map((msg) => (
            <div key={msg} style={{ marginTop: 4, fontSize: 11.5, fontWeight: 650, color: msg.startsWith("Overlaps") || msg.startsWith("Outside") ? "#B45309" : C.error }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 12, marginRight: 4 }} />
              {msg}
            </div>
          ))}
        </td>
        {showTimes && (
          <td style={{ padding: "6px 8px" }}>
            <input
              type="time"
              step={60}
              disabled={off}
              value={row.start || ""}
              aria-label={`${slot.label} from`}
              onChange={(e) => updateFields(slot, { start: e.target.value })}
              style={timeInp}
            />
          </td>
        )}
        {showTimes && (
          <td style={{ padding: "6px 8px" }}>
            <input
              type="time"
              step={60}
              disabled={off}
              value={row.end || ""}
              aria-label={`${slot.label} to`}
              onChange={(e) => updateFields(slot, { end: e.target.value })}
              style={timeInp}
            />
          </td>
        )}
        <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
          {off
            ? iconBtn("Restore", () => turnOn(slot.id), "ti-arrow-back-up")
            : iconBtn("Delete", () => deleteActivity(slot.id), "ti-trash", true)}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap",
      }}>
        <p style={{ fontSize: 12.5, color: C.muted, margin: 0, lineHeight: 1.4 }}>{hint}</p>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            onClick={addActivity}
            style={{
              border: `1px solid ${C.primary}`,
              background: C.primarySoft,
              color: C.primaryDark,
              fontSize: 12,
              fontWeight: 750,
              cursor: "pointer",
              borderRadius: 8,
              padding: "6px 10px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="ti ti-plus" style={{ fontSize: 14 }} />
            Add activity
          </button>
          {showTimes && onResync && (
            <button
              type="button"
              onClick={onResync}
              title="Re-fit every activity to the day, lunch and breaks set in Basics"
              style={{
                border: `1px solid ${C.border}`, background: "#fff", color: C.text,
                fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 8, padding: "6px 10px",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <i className="ti ti-refresh" style={{ fontSize: 14 }} />
              Fit to Basics
            </button>
          )}
          {changed && (
            <button
              type="button"
              onClick={() => onChange?.({})}
              style={{
                border: "none", background: "transparent", color: C.primary,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >
              {showTimes ? "Reset to Basics times" : "Reset"}
            </button>
          )}
        </div>
      </div>

      <div
        style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const fromOff = (overrides[dragId] || {}).mode === "hide";
          if (fromOff && overId !== "__off__") onDropOn();
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: showTimes ? 560 : 360 }}>
          <thead>
            <tr style={{ background: "#F8FAFC" }}>
              {(showTimes ? ["", "Name & content", "From", "To", ""] : ["", "Name & content", ""]).map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  style={{
                    textAlign: "left",
                    padding: "8px 10px",
                    fontSize: 11,
                    fontWeight: 750,
                    color: C.muted,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {onList.map((slot) => rowWrap(slot))}
          </tbody>
        </table>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOverId("__off__");
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDropOff();
        }}
        style={{
          marginTop: 10,
          border: `1.5px dashed ${overId === "__off__" ? C.primary : C.border}`,
          background: overId === "__off__" ? C.primarySoft : "#F8FAFC",
          borderRadius: 10,
          padding: "10px 12px",
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: offList.length ? 8 : 0 }}>
          <i className="ti ti-eye-off" style={{ fontSize: 16, color: C.muted }} />
          <span style={{ fontSize: 12.5, fontWeight: 750, color: C.text }}>Deleted</span>
          <span style={{ fontSize: 12, color: C.muted }}>
            {offList.length ? "Restore to put it back on the schedule." : "Delete an activity, or drag it here. It drops off the tables until you restore it."}
          </span>
        </div>
        {offList.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <tbody>
              {offList.map((slot) => rowWrap(slot, { off: true }))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: "daily", label: "All days", icon: "ti-calendar-week" },
  { id: "groups", label: "By day range", icon: "ti-calendar-stats" },
  { id: "homework", label: "Tonight's practice", icon: "ti-moon-stars" },
];

export default function DayActivityEditor({ value, onChange, timing = {}, dayGroups = [], onDayGroupsChange, numDays = 5 }) {
  const [tab, setTab] = useState("daily");
  const [openDayGroup, setOpenDayGroup] = useState(null);
  const bundle = splitStoredOverrides(value, { keepRaw: true });

  const setPart = (key, next) => {
    const empty = !next || Object.keys(next).length === 0;
    onChange?.({
      ...bundle,
      [key]: next,
      ...(empty && key === "daily" ? { timingKey: "" } : {}),
    });
  };

  const clock = (t) => (t ? formatTime12(t) : "");
  const filledBreaks = (timing.breaks || []).filter((b) => b?.start && b?.end);
  const summaryBits = [
    timing.dailyStart && timing.dailyEnd
      ? `Day ${clock(timing.dailyStart)}–${clock(timing.dailyEnd)}`
      : "",
    timing.lunchStart && timing.lunchEnd
      ? `Lunch ${clock(timing.lunchStart)}–${clock(timing.lunchEnd)}`
      : "",
    filledBreaks.length
      ? `Break ${filledBreaks.map((b) => `${clock(b.start)}–${clock(b.end)}`).join(", ")}`
      : "",
    timing.startDate && timing.endDate
      ? `${timing.startDate} → ${timing.endDate}`
      : "",
    timing.numDays ? `${timing.numDays} days` : "",
  ].filter(Boolean);

  return (
    <div>
     
      {summaryBits.length > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: `1px solid ${C.border}`,
            background: C.primarySoft,
            borderRadius: 10,
            fontSize: 12.5,
            color: C.text,
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontWeight: 750, marginBottom: 2 }}>Synced from Basics</div>
          <div>{summaryBits.join(" · ")}</div>
          <div style={{ color: C.muted, marginTop: 4, fontSize: 12 }}>
            Lunch and Break rows use the Basics clocks automatically; the other activities are spread across the rest of the day (your order and any swaps are kept).  
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {TABS.map((t) => {
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 750,
                cursor: "pointer",
                border: `1.5px solid ${on ? C.primary : C.border}`,
                background: on ? C.primary : "#fff",
                color: on ? "#fff" : C.text,
              }}
            >
              <i className={`ti ${t.icon}`} style={{ fontSize: 15 }} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "daily" ? (
        <SlotTable
          catalog={DAY_ACTIVITY_SLOTS}
          value={bundle.daily}
          onChange={(next) => setPart("daily", next)}
          hint="This list powers the daily study plan table. Add, rename, edit content, change times, or delete. Drag to swap times. Changes show in Daily study plan."
          dayWindow={{ start: timing.dailyStart, end: timing.dailyEnd }}
          onResync={() => onChange?.({ ...bundle, timingKey: "" })}
          showTimes
        />
      ) : tab === "homework" ? (
        <SlotTable
          catalog={HOMEWORK_ACTIVITY_SLOTS}
          value={bundle.homework}
          onChange={(next) => setPart("homework", next)}
          hint="This list is tonight's practice plan. Add, rename, edit, or delete types. Drag to reorder. No clock times."
          showTimes={false}
        />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <p style={{ margin: 0, fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>
              Create activity sets for different parts of the plan. Each range overrides “All days” for those working days.
            </p>
            <button type="button" onClick={() => {
              const maxDay = Math.max(1, Number(numDays) || 1);
              const startDay = Math.min(maxDay, (Number(dayGroups[dayGroups.length - 1]?.endDay) || 0) + 1);
              const id = `day-group-${Date.now()}`;
              onDayGroupsChange?.([...dayGroups, { id, startDay, endDay: Math.min(maxDay, startDay + 1), activities: JSON.parse(JSON.stringify(bundle.daily || {})) }]);
              setOpenDayGroup(id);
            }} style={{ border: `1px solid ${C.primary}`, background: C.primarySoft, color: C.primary, borderRadius: 8, padding: "8px 11px", fontWeight: 750, cursor: "pointer" }}>
              <i className="ti ti-plus" style={{ marginRight: 5 }} />Add day range
            </button>
          </div>
          {!dayGroups.length && (
            <div style={{ padding: 18, border: `1px dashed ${C.border}`, borderRadius: 10, background: "#FAFBFC", color: C.muted, fontSize: 12.5 }}>
              No ranges yet. Add Days 1–2, then set the activities for that phase.
            </div>
          )}
          {dayGroups.map((group, index) => (
            <section key={group.id || index} style={{ padding: 12, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.primary}`, borderRadius: 10, background: "#fff", display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <strong style={{ fontSize: 12.5, color: C.text }}>Days {group.startDay}–{group.endDay}</strong>
                <span style={{ fontSize: 10.5, color: C.muted }}>Working-day range {index + 1}</span>
              </div>
              <div style={{ display: "flex", alignItems: "end", gap: 10, flexWrap: "wrap" }}>
                <label style={{ display: "grid", gap: 4, color: C.text, fontSize: 11.5, fontWeight: 700 }}>
                  From day
                  <input type="number" min="1" max={numDays} value={group.startDay ?? 1} onChange={(e) => {
                    const next = [...dayGroups]; next[index] = { ...group, startDay: Math.max(1, Number(e.target.value) || 1) }; onDayGroupsChange?.(next);
                  }} style={timeInp} />
                </label>
                <label style={{ display: "grid", gap: 4, color: C.text, fontSize: 11.5, fontWeight: 700 }}>
                  Through day
                  <input type="number" min="1" max={numDays} value={group.endDay ?? 2} onChange={(e) => {
                    const next = [...dayGroups]; next[index] = { ...group, endDay: Math.max(1, Number(e.target.value) || 1) }; onDayGroupsChange?.(next);
                  }} style={timeInp} />
                </label>
                <button type="button" onClick={() => setOpenDayGroup(openDayGroup === group.id ? null : group.id)} style={{ border: `1px solid ${C.border}`, color: C.primary, background: "#fff", borderRadius: 8, padding: "8px 10px", cursor: "pointer", fontWeight: 700, fontSize: 11.5 }}>
                  <i className={`ti ${openDayGroup === group.id ? "ti-chevron-up" : "ti-adjustments"}`} style={{ marginRight: 5 }} />{openDayGroup === group.id ? "Hide activities" : "Configure activities"}
                </button>
                <button type="button" onClick={() => onDayGroupsChange?.(dayGroups.filter((_, i) => i !== index))} style={{ marginLeft: "auto", border: "1px solid #FECACA", color: C.error, background: "#FEF2F2", borderRadius: 8, padding: "8px 10px", cursor: "pointer" }}>
                  <i className="ti ti-trash" style={{ marginRight: 4 }} />Remove group
                </button>
              </div>
              {Number(group.endDay) < Number(group.startDay) && <p style={{ margin: 0, color: C.error, fontSize: 12 }}>Last day must be on or after first day.</p>}
              {openDayGroup === group.id && <SlotTable
                catalog={DAY_ACTIVITY_SLOTS}
                value={group.activities || {}}
                onChange={(activities) => {
                  const next = [...dayGroups]; next[index] = { ...group, activities }; onDayGroupsChange?.(next);
                }}
                hint={`Activities for working days ${group.startDay}–${group.endDay}. Names and task briefs are sent with these exact day numbers.`}
                dayWindow={{ start: timing.dailyStart, end: timing.dailyEnd }}
                showTimes
              />}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}


